import React, { useEffect, useState } from 'react';
import { Drawer, Box, IconButton, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Typography, Button, Input, Textarea } from '@/components/ui';

export interface PermissionFormValues {
  id?: string;
  name: string;
  description: string;
}

export interface FormErrors {
  name?: string;
  description?: string;
}

export interface PermissionFormDrawerProps {
  open: boolean;
  initialValues?: PermissionFormValues | null;
  onClose: () => void;
  onSubmit: (values: PermissionFormValues) => void;
}

const drawerWidth = 420;

// Validation helper functions
const validatePermissionName = (name: string): string | undefined => {
  if (!name.trim()) {
    return 'Permission name is required';
  }
  if (name.trim().length > 30) {
    return 'Permission name must not exceed 30 characters';
  }
  return undefined;
};

const validateDescription = (description: string): string | undefined => {
  if (description.trim().length > 50) {
    return 'Description must not exceed 50 characters';
  }
  return undefined;
};

const PermissionFormDrawer: React.FC<PermissionFormDrawerProps> = ({ open, initialValues, onClose, onSubmit }) => {
  const [values, setValues] = useState<PermissionFormValues>({ name: '', description: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const isEdit = Boolean(initialValues?.id);

  useEffect(() => {
    if (initialValues) {
      setValues({ 
        id: initialValues.id, 
        name: initialValues.name || '', 
        description: initialValues.description || '' 
      });
    } else {
      setValues({ name: '', description: '' });
    }
    // Clear errors when form resets
    setErrors({});
  }, [initialValues]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setValues(prev => ({ ...prev, [name]: value }));

    // Real-time validation
    let error: string | undefined;
    switch (name) {
      case 'name':
        error = validatePermissionName(value);
        break;
      case 'description':
        error = validateDescription(value);
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    const nameError = validatePermissionName(values.name);
    const descriptionError = validateDescription(values.description);
    
    const newErrors: FormErrors = {
      name: nameError,
      description: descriptionError,
    };
    
    setErrors(newErrors);
    
    // Check if there are any errors
    const hasErrors = Object.values(newErrors).some(error => error !== undefined);
    
    if (hasErrors) {
      return; // Don't submit if there are validation errors
    }
    
    onSubmit({ 
      ...values, 
      name: values.name.trim(),
      description: values.description.trim()
    });
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleClose} PaperProps={{ sx: { width: drawerWidth } }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={2}>
        <Typography variant="h6">{isEdit ? 'Edit Permission' : 'Create Permission'}</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box component="form" onSubmit={handleSubmit} px={2} py={2} display="flex" flexDirection="column" gap={2}>
        <Typography variant="subtitle1" fontWeight="medium">
          Permission Details
        </Typography>
        <Input
          label="Permission Name"
          name="name"
          placeholder="Enter permission name"
          value={values.name}
          onChange={handleChange}
          fullWidth
          // required
          error={!!errors.name}
          helperText={errors.name}
          inputProps={{ maxLength: 30 }}
        />
        <Textarea
          label="Description"
          name="description"
          placeholder="Enter permission description"
          value={values.description}
          onChange={handleChange}
          fullWidth
          minRows={3}
          maxRows={6}
          autoResize={true}
          error={!!errors.description}
          helperText={errors.description}
          inputProps={{ maxLength: 50 }}
        />

        <Box display="flex" gap={1} mt={1}>
          <Button type="submit" variant="primary">
            {isEdit ? 'Update' : 'Create'}
          </Button>
          <Button type="button" variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default PermissionFormDrawer;


